<template>
  <div class="sovi-pricing-page sovi-home">
    <router-link to="/" class="sovi-pricing-page__logo">
      <sovi-logo :size="48" dark />
    </router-link>

    <v-menu offset-y>
      <template #activator="{ props }">
        <v-btn
          class="language-menu"
          size="default"
          color="#eca1c2"
          v-bind="props"
        >
          <template #prepend>
            <v-icon icon="mdi-web" />
          </template>

          {{ $i18n.locale.toUpperCase() }}
        </v-btn>
      </template>

      <v-list>
        <v-list-item
          v-for="option in $i18n.availableLocales"
          :key="option"
          class="align-items-center"
          @click="onLocaleChange(option)"
        >
          <v-row align="center" no-gutters>
            {{ option.toUpperCase() }}
          </v-row>
        </v-list-item>
      </v-list>
    </v-menu>

    <sovi-content-nav />

    <sovi-home-pricing style="padding-top: 100px" />

    <div class="sovi-pricing-page__login">
      <sovi-home-login />
    </div>

    <sovi-home-card
      id="helpommin"
      background="/images/desk.webp"
      src="/images/sovi-web-1.jpg"
      :title="t('agreeEasily')"
      fixed
    >
      <strong>{{ t('electronicSignatureService') }}</strong>

      <p>
        {{ t('contentText1') }}
      </p>

      <p>
        {{ t('contentText1b') }}
      </p>

      <template #button>
        <div style="display: flex; gap: 12px; flex-wrap: wrap">
          <router-link to="/sahkoinen-allekirjoitus" class="text-none">
            <v-btn color="primary" class="text-none" size="small">
              {{ t('linkEsignature') }}
              <template #append>
                <v-icon size="small">mdi-arrow-right</v-icon>
              </template>
            </v-btn>
          </router-link>

          <router-link to="/ukk" class="text-none">
            <v-btn color="primary" class="text-none" size="small">
              {{ t('linkFaq') }}
              <template #append>
                <v-icon size="small">mdi-arrow-right</v-icon>
              </template>
            </v-btn>
          </router-link>
        </div>
      </template>
    </sovi-home-card>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { definePage } from 'vue-router/auto';
import { useRouteMeta } from '@/composables/useRouteMeta';
import { useAuthorizationStore } from '@/stores/authorization';
import { loadRecaptcha } from '@/plugins/recaptcha';
import SoviHomePricing from '@/components/home/SoviHomePricing.vue';
import SoviHomeLogin from '@/components/home/SoviHomeLogin.vue';

definePage({
  meta: {
    hero: false,
    restricted: false,
    maxContainer: true,
  },
});

useRouteMeta({
  title: 'Hinnasto | Sovi.fi',
  description:
    'Sovi.fi-palvelun hinnat. Ei kuukausimaksua, ei sitovaa sopimusta. Maksat vain käytöstä. Aloita ilmaiseksi.',
});

const { locale } = useI18n({ useScope: 'global' });

const { t } = useI18n();

const theme = useTheme();

const { isAuthorized } = useAuthorizationStore();

const onLocaleChange = (newLocale: string) => {
  locale.value = newLocale;
  localStorage.setItem('sovi-locale', newLocale);
};

onMounted(() => {
  loadRecaptcha();

  const storedLocale = localStorage.getItem('sovi-locale');

  if (storedLocale && storedLocale !== locale.value) {
    locale.value = storedLocale;
  }

  theme.global.name.value = 'light';

  isAuthorized();
});
</script>

<style lang="scss">
.grecaptcha-badge {
  visibility: hidden;
}

.sovi-pricing-page {
  position: relative;
  background-color: #fff;
  font-size: clamp(18px, 2vw, 22px);

  &__logo {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 2;
  }

  .language-menu {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    box-shadow:
      rgba(0, 0, 0, 0.15) 0px 15px 25px,
      rgba(0, 0, 0, 0.05) 0px 5px 10px;
  }

  .home-title {
    --title-color: rgb(var(--v-theme-primary));

    &.alt {
      --title-color: #eca1c2;
    }

    &::first-letter {
      padding-bottom: 2px;
      box-shadow: inset 0 calc(-1 * clamp(4px, 0.5vw, 6px)) 0 0
        var(--title-color);
    }
  }

  &__login {
    background-color: #fff;
    padding: clamp(32px, 5vw, 64px) clamp(16px, 5vw, 32px);

    .sovi-home__login-form {
      display: grid;
      gap: clamp(16px, 3vw, 32px);
      position: relative;
      z-index: 1;

      h1 {
        font-size: clamp(24px, 3vw, 32px);
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "fi": {
    "agreeEasily": "Sovi helpommin",
    "electronicSignatureService": "Helppokäyttöinen ja turvallinen kotimainen sähköinen allekirjoituspalvelu",
    "contentText1": "Sovi on kotimainen sähköinen allekirjoituspalvelu, jossa sopimuksesi säilyvät järjestyksessä. Palvelussa tehty sähköinen allekirjoitus on sitova ja allekirjoitetun asiakirjan aitous on sähköisesti todennettavissa.",
    "contentText1b": "Palvelu on heti käytössäsi ilman riskiä tai kuukausimaksua - kokeile ilmaiseksi!",
    "linkEsignature": "Lue lisää",
    "linkFaq": "Usein kysytyt kysymykset"
  },
  "en": {
    "agreeEasily": "Agree easier",
    "electronicSignatureService": "Safe and easy-to-use e-signature platform",
    "contentText1": "Sovi is a PDF document signing service, which keeps your contracts in order. Sovi e-signatures are legally binding and all signatures can be electronically verified.",
    "contentText1b": "Start using Sovi right away without any risk or monthly fees - try it for free!",
    "linkEsignature": "Read more",
    "linkFaq": "Frequently asked questions"
  }
}
</i18n>
